import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MenuIcon, SearchIcon, ShoppingBagIcon, StarIcon, XIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { mainNav, usps } from '../../data/navigation';
import { Logo } from '../brand/Logo';
import { SearchBox } from './SearchBox';
import { MobileMenu } from './MobileMenu';
const ease = [0.23, 1, 0.32, 1] as const;
export function Header() {
  const {
    count,
    open
  } = useCart();
  const {
    pathname
  } = useLocation();
  const reduce = useReducedMotion();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [usp, setUsp] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setUsp(u => (u + 1) % usps.length), 4000);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => setSearchOpen(false), [pathname]);
  const navItems = mainNav.filter(n => n.to !== '/collectie/alles');
  const linkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `whitespace-nowrap py-1 text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors duration-150 ${isActive ? 'text-ink underline decoration-2 underline-offset-[10px]' : 'text-ink/70 hover:text-ink'}`;
  return <>
      <header className="sticky top-0 z-40 bg-white" data-id="element-216" data-mp="3k2gm9fy0fkra">
        <div className="bg-ink text-white" data-id="element-217" data-mp="3k3rr7j36yyjz">
          <div className="mx-auto grid h-9 max-w-7xl grid-cols-1 items-center px-5 text-xs sm:grid-cols-[1fr_auto_1fr] lg:px-8" data-id="element-218" data-mp="3p11nnip00ahb">
            <div className="hidden items-center gap-5 sm:flex" data-id="element-219" data-mp="3tzbifs6t992e">
              <span className="font-semibold uppercase tracking-[0.16em]" data-id="element-220" data-mp="1wc1tejzw03c">Beauty</span>
              <Link to="/living" className="uppercase tracking-[0.16em] text-white/55 transition-colors duration-150 hover:text-white" data-id="element-221" data-mp="1u94sakfd6p4">
                Living
              </Link>
            </div>
            <div className="relative h-9 overflow-hidden text-center" aria-live="polite" data-id="element-222" data-mp="1ddlkvzz7oq44">
              <AnimatePresence mode="wait" initial={false} data-id="element-223" data-mp="3aehkqal0fr4f">
                <motion.p key={usp} initial={reduce ? false : {
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={reduce ? undefined : {
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.2,
                ease
              }} className="flex h-9 items-center justify-center whitespace-nowrap font-medium" data-id="element-224" data-mp="35ciix54x529l">
                  {usps[usp]}
                </motion.p>
              </AnimatePresence>
            </div>
            <p className="hidden items-center justify-end gap-1.5 md:flex" data-id="element-225" data-mp="30fs4em8rieqa">
              <StarIcon className="h-3 w-3 fill-white" strokeWidth={0} aria-hidden="true" data-id="element-226" data-mp="2vmft1dt3g4fh" />
              4,8 · 3.100+ reviews
            </p>
          </div>
        </div>

        <div className="border-b border-line" data-id="element-227" data-mp="22z344wzgnkj0">
          <div className="mx-auto grid h-16 max-w-7xl grid-cols-[1fr_auto_1fr] items-center px-5 lg:h-20 lg:px-8" data-id="element-228" data-mp="22ya6clihc5d0">
            <div className="flex items-center gap-1" data-id="element-229" data-mp="ifhg118sagom">
              <button type="button" onClick={() => setMenuOpen(true)} className="-ml-2 grid h-10 w-10 place-items-center lg:hidden" aria-label="Menu openen" data-id="element-230" data-mp="n7r40bqc96o9">
                <MenuIcon className="h-5 w-5" data-id="element-231" data-mp="9e9msxl4k61e" />
              </button>
              <button type="button" onClick={() => setSearchOpen(s => !s)} className="grid h-10 w-10 place-items-center lg:hidden" aria-label={searchOpen ? 'Zoeken sluiten' : 'Zoeken'} aria-expanded={searchOpen} data-id="element-232" data-mp="efp54hvx3yg3">
                {searchOpen ? <XIcon className="h-5 w-5" data-id="element-233" data-mp="s8yckd0b29vz" /> : <SearchIcon className="h-5 w-5" data-id="element-234" data-mp="x55k2a8cntg1" />}
              </button>
              <div className="hidden w-full max-w-[260px] lg:block" data-id="element-235" data-mp="x7hxqb72rhna">
                <SearchBox data-id="element-236" data-mp="3cv8d3pf5sqob" />
              </div>
            </div>

            <Link to="/" aria-label="AMIRA Beauty, naar home" data-id="element-237" data-mp="3hw60cgf10awo">
              <Logo data-id="element-238" data-mp="1055zehsg7l2" />
            </Link>

            <div className="flex justify-end" data-id="element-239" data-mp="116tiifzqmjo">
              <button type="button" onClick={open} className="flex h-10 items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em]" aria-label={`Winkelwagen, ${count} artikelen`} data-id="element-240" data-mp="11ztn0kg2dsu">
                <span className="relative" data-id="element-241" data-mp="3jfpkw63swi7h">
                  <ShoppingBagIcon className="h-5 w-5" aria-hidden="true" data-id="element-242" data-mp="jyr8mxsye6r3" />
                  <span className="absolute -right-2 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-pink px-1 text-[10px] font-semibold tabular-nums text-white" data-id="element-243" data-mp="jwwv44k0hgx1">
                    {count}
                  </span>
                </span>
                <span className="hidden sm:inline" data-id="element-244" data-mp="5y6onybay4w0">Winkelwagen</span>
              </button>
            </div>
          </div>

          <nav aria-label="Shop" className="hidden border-t border-line lg:block" data-id="element-245" data-mp="2vq4wv8ssiurc">
            <ul className="mx-auto flex h-12 max-w-7xl items-center justify-center gap-9 px-8" data-id="element-246" data-mp="2qopczk2cmuaj">
              {navItems.map(item => <li key={item.to} data-id="element-247" data-mp="2gjpf79z7yosp">
                  <NavLink to={item.to} className={linkClass} data-id="element-248" data-mp="2lk3pkyoamatw">
                    {item.label}
                  </NavLink>
                </li>)}
              <li data-id="element-249" data-mp="2qhn5e30n2vny">
                <NavLink to="/over-amira" className={linkClass} data-id="element-250" data-mp="2vag9qps741zi">
                  Ons verhaal
                </NavLink>
              </li>
              <li data-id="element-251" data-mp="2zb06vtcrr5ln">
                <Link to="/living" className="whitespace-nowrap py-1 text-[13px] font-semibold uppercase tracking-[0.12em] text-pink transition-colors duration-150 hover:text-berry" data-id="element-252" data-mp="2zbs04xiw8r8r">
                  AMIRA Living
                </Link>
              </li>
            </ul>
          </nav>

          <AnimatePresence data-id="element-253" data-mp="3e7zg1ln9r6i8">
            {searchOpen && <motion.div initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.18,
            ease
          }} className="border-t border-line bg-white lg:hidden" data-id="element-254" data-mp="1ewgcjmkqiqfy">
                <div className="px-5 py-3" data-id="element-255" data-mp="16w0hkydn01op">
                  <SearchBox autoFocus onNavigate={() => setSearchOpen(false)} data-id="element-256" data-mp="16xblqzazvbgc" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} data-id="element-257" data-mp="1gs3mgbzviefc" />
    </>;
}